import { HttpErrorResponse } from '@angular/common/http';
import { inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BackendService, DialogService } from '@core/services';
import { BaseComponent } from '@shared/classes';
import { AuthErrorModalComponent } from '@shared/components/modals';
import { FormFieldType } from '@shared/constants';
import {
  AuthPayload,
  AuthResponse,
  FormField,
  InputValidations,
} from '@shared/models';

export abstract class AuthBaseComponent extends BaseComponent {
  public formSubmitted: WritableSignal<boolean> = signal(false);
  public showErrorMessage: WritableSignal<boolean> = signal(false);
  public controlErrorMessages: Record<string, Record<string, string>> = {};

  public readonly FormFieldType: typeof FormFieldType = FormFieldType;

  protected readonly router: Router = inject(Router);
  protected readonly dialog: DialogService = inject(DialogService);
  protected readonly avtivatedRoute: ActivatedRoute = inject(ActivatedRoute);
  protected readonly backendService: BackendService = inject(BackendService);

  protected authenticate(payload: AuthPayload): void {
    const to: string =
      this.avtivatedRoute.snapshot.queryParams['to'] || 'current';
    this.backendService
      .login(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: AuthResponse) => {
          const untienotsId = result.untienots_id;
          this.router.navigate([untienotsId, to], {
            queryParamsHandling: 'merge',
            queryParams: { from_login: 'true' },
          });
        },
        error: (error: HttpErrorResponse) => {
          this.formSubmitted.set(false);
          if (
            this.loyaltyChallengeConfig.components.modals.authError &&
            error.status === 403 &&
            error.error?.detail.includes('This account is not allowed to play.')
          ) {
            // Open the error dialog
            this.dialog.open(AuthErrorModalComponent);
          } else {
            this.showErrorMessage.set(true);
          }
        },
      });
  }

  protected initForm(fields: FormField[]): FormGroup {
    // Init Controls
    const controls: { [key: string]: AbstractControl } = {};

    // Construct controls
    for (const field of fields) {
      const validations: ValidatorFn[] = this.constructFormValidations(
        field.formControlName,
        field.validations
      );
      controls[field.formControlName] = new FormControl(
        field.defaultValue,
        validations
      );
    }

    // init FormGroup
    return new FormGroup(controls);
  }

  private constructFormValidations(
    formControlName: string,
    inputValidations: InputValidations[]
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    for (const item of inputValidations) {
      switch (item.type) {
        case 'pattern':
          validators.push(Validators.pattern(<string>item.pattern));
          break;
        case 'email':
          validators.push(Validators.email);
          break;
        case 'required':
          validators.push(Validators.required);
          break;
        case 'requiredTrue':
          validators.push(Validators.requiredTrue);
          break;
        default:
          break;
      }
      if (item.message)
        this.controlErrorMessages[formControlName] = {
          ...this.controlErrorMessages[formControlName],
          [item.type]: item.message,
        };
    }
    return validators;
  }
}
